import type { MaybeRef, UnwrapRef } from 'vue';
import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { TIENDANUBE_AUTHORIZATION_KEY_PREFIX } from './storage';
import type {
  TiendanubeBindResponse,
  TiendanubeConnectResponse,
  TiendanubeInstallationResponse,
} from '@/modules/flokee/services/flok.service.interface';
import { flokService } from '@/modules/flokee/services/flok.service';
import { useEcommerceStore } from '@/modules/flokee/stores/ecommerce.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { BusinessUnitKey } from '@/modules/bu/registry.base';
import { queryClient } from '@/plugins/vue-query';
import { handleAxiosError } from '@/utils/error.utils';

interface InstallationState {
  // `initiated_for_ecommerce_id` is ours, not CXM's: the store an in-app
  // authorization was started for, so the install can be matched to it rather
  // than to whatever store happens to be current when it returns. It is what the
  // callback has to go on, because the routing decision there happens before any
  // bind call and calling bind to find out would create an order for what may be
  // a reconnect.
  // `binding` is Flokee's answer for this installation once `bind` has given it
  // one: the order and ecommerce it is tied to. Where it exists it is the
  // authority, and nothing downstream has to guess from what we recorded.
  pending_tiendanube_installation: MaybeRef<
    | (TiendanubeInstallationResponse & {
        initiated_for_ecommerce_id?: number;
        binding?: TiendanubeBindResponse;
      })
    | undefined
  >;
}

type InstallationHost = UnwrapRef<InstallationState> & {
  needsFlokeeOrder: boolean;
  installationNeedsOwnOrder: boolean;
  getPendingTiendanubeInstallation(): UnwrapRef<InstallationState>['pending_tiendanube_installation'];
  clearPendingTiendanubeInstallation(): void;
};

const PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY = 'pending_tiendanube_installation';
// The store an in-app authorization was started for lives in a `localStorage`
// key OF ITS OWN, named after the OAuth state that authorization minted.
//
// `localStorage`, not `sessionStorage`: the widgets flow opens its tab with
// `window.open` BEFORE the authorization is minted, and a new tab gets a copy
// of sessionStorage taken at that moment, so the callback tab would never see
// this. One key per state, not a map in one key: the object is the unit of
// write, so two tabs recording at the same instant each write the whole map
// back and the last write drops the other's entry. A key of its own cannot be
// clobbered that way, and the returning callback reads exactly the key its own
// state names. Nothing here needs reactivity — it is written once and read once
// — so it is plain `localStorage` rather than `useStorage`. The prefix itself
// lives in this integration's storage module, because every wholesale clear of stored
// state has to know what it must not wipe.

function tiendanubeAuthorizationKey(state: string): string {
  return `${TIENDANUBE_AUTHORIZATION_KEY_PREFIX}${state}`;
}

// Anything that is not what `recordTiendanubeAuthorization` wrote — an older
// build's value, a hand edit — reads as nothing rather than as a store id.
function readTiendanubeAuthorization(state: string): number | undefined {
  try {
    const entry = JSON.parse(localStorage.getItem(tiendanubeAuthorizationKey(state)) ?? 'null');

    return Number.isInteger(entry?.ecommerce_id) ? entry.ecommerce_id : undefined;
  } catch {
    return undefined;
  }
}

function isTiendanubeBindResponse(value: unknown): value is TiendanubeBindResponse {
  const binding = value as Partial<TiendanubeBindResponse> | null | undefined;

  return (
    Number.isInteger(binding?.store_id) &&
    Number.isInteger(binding?.order_id) &&
    Number.isInteger(binding?.ecommerce_id)
  );
}

function isTiendanubeConnectResponse(value: unknown): value is TiendanubeConnectResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const response = value as Partial<TiendanubeConnectResponse>;

  return Number.isInteger(response.store_id) && Number.isInteger(response.ecommerce_id);
}

function isTiendanubeInstallationResponse(value: unknown): value is TiendanubeInstallationResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const response = value as Partial<TiendanubeInstallationResponse>;

  return (
    typeof response.installation_token === 'string' &&
    response.installation_token.length > 0 &&
    typeof response.installation_expires_at === 'string'
  );
}

const pendingTiendanubeInstallationSerializer = {
  read(value: string): TiendanubeInstallationResponse {
    const installation = StorageSerializers.object.read(value);

    if (!isTiendanubeInstallationResponse(installation)) {
      throw new TypeError('Invalid pending Tiendanube installation storage value.');
    }

    return installation;
  },
  write(value: TiendanubeInstallationResponse | undefined): string {
    return value === undefined ? 'null' : StorageSerializers.object.write(value);
  },
};

function installationState(): InstallationState {
  return {
    pending_tiendanube_installation: useStorage<TiendanubeInstallationResponse | undefined>(
      PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY,
      undefined,
      sessionStorage,
      {
        serializer: pendingTiendanubeInstallationSerializer,
        onError: () => sessionStorage.removeItem(PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY),
      },
    ),
  };
}

const installationGetters = {
  // CXM `resolveFlokeeEcommerceId` aborts 422 when the current order has no
  // Flokee plan, and the product has no way back from there (FLOK-1714), so
  // the installation decides the BU here, not the URL or `cusp_bu`.
  //
  // Fails closed: only a summary that NAMES another BU diverts the merchant.
  // `getOrderSummary()` swallows its own failures, so an undefined summary
  // means "unknown" — reading it as "not Flokee" would bill a second order to
  // someone who already has a Flokee one, while an absent one only leaves the
  // install retryable. Callers keep their own `current_order_id` check, and
  // it is load-bearing: no order means no summary, so this is false there.
  needsFlokeeOrder: (state: UnwrapRef<InstallationState>) => {
    if (!state.pending_tiendanube_installation) return false;
    const order_bu = useOrderSummaryStore().orderSummary?.business_unit;
    return !!order_bu && order_bu !== BusinessUnitKey.Flokee;
  },
  // THE THREE INVARIANTS OF THIS INSTALLATION FLOW. Every cross-tab bug this
  // flow has had was a variant of one of these, so they are the contract for
  // everything that reads `installationNeedsOwnOrder` or writes to a store:
  //
  //  (1) An identity check runs IMMEDIATELY BEFORE THE WRITE, and never from
  //      behind a branch that can skip it. A route guard runs once, at entry,
  //      and the world moves afterwards: it is a fast path that avoids a
  //      pointless navigation, not what makes a write safe.
  //      `refreshInstallationStoreState()` below is the read these sit on.
  //  (2) A value used for identity is CAPTURED BEFORE ANY AWAIT that could
  //      invalidate it. The store that is current when an operation STARTS is
  //      the store it is about, whatever is current when it returns. It is
  //      also never STORED where another tab's write can clobber it.
  //  (3) A FAILED READ IS UNKNOWN, NEVER 'DIFFERENT'. Unknown refuses to act:
  //      it never authorises creating a billable order, and never authorises
  //      writing to a store. Those are opposite answers to the same absence,
  //      which is why routing asks `installationNeedsOwnOrder` and everything
  //      that writes asks `installationMayWriteToCurrentStore`.
  //
  // FLOK-1717.
  //
  // What the callback, the post-login route and the account step all ask: is
  // the current order the one this installation should complete against? Two
  // ways it is not — the order is another product's, or its store is already
  // connected, and the wizard and provider step would rename that store,
  // repoint its URL and bind the new token over the old.
  installationNeedsOwnOrder(): boolean {
    const installation = this.pending_tiendanube_installation;
    if (!installation) return false;
    // Bound: Flokee named this installation's order, so there is nothing to
    // infer. It needs to be ON that order, and anything else — including
    // another tab moving the account while the wizard is open — says so. The
    // account step asks `bind` again, which is idempotent by store and makes
    // that same order current again. The business unit is answered here too:
    // `bind` only binds a Flokee package plan, so a summary naming another
    // product is a summary of the order we are NOT on. FLOK-1716.
    if (installation.binding)
      return installation.binding.order_id !== useAuthStore().user?.current_order_id;
    // Another product's order is never this install's, whoever started it.
    if (this.needsFlokeeOrder) return true;
    // No recorded store: the marketplace started this, and a marketplace
    // install gets its OWN order, whatever is current. It used to accept any
    // store that was not already connected, which is not an identity at all —
    // a second unconnected store selected in another tab passed the same test,
    // and the install would have been written to it. Its own order is exact
    // and, with `bind`, free: idempotent by store, so asking cannot duplicate.
    //
    // Which is why this answer comes BEFORE the store is read: `bind` keys on
    // the store in the ticket, not on anything we loaded, so an unknown
    // current store cannot make it wrong — and refusing for one left the
    // install at the create-ecommerce step, which neither creates nor writes
    // while an installation is pending. No store at all is the ordinary
    // first-install shape, too.
    const started_for = installation.initiated_for_ecommerce_id;
    if (!started_for) return true;

    // Which store this install is for, not who started it. An in-app
    // authorization records the store it was started for, so this is a
    // comparison of identities: the same store means the install really is
    // that store's and it is the one to edit; a different one means another
    // tab moved the current store underneath us, and writing to it would
    // overwrite a store nobody asked about, so the install goes and gets its
    // own order — an order rather than a store.
    //
    // Invariant (3): no store loaded is UNKNOWN, not "a different store".
    // `getEcommerces` swallows a failed fetch and `clear` leaves none, so an
    // empty store here says nothing about which store a RECONNECT is for —
    // and reading it as different bills a new order to one every time the
    // network blips. Unknown refuses: every path that writes needs the store
    // anyway, so this one stops at the next step and retries the read.
    const current_store = useEcommerceStore().currentEcommerce;
    if (!current_store) return false;

    // Two ways the current order is not the one to complete against, and the
    // second is why routing asks about the ORDER and not only the store: the
    // selection is re-pointed only for an order whose summary loaded, so a tab
    // that switches Flokee orders while the summary fails leaves this naming
    // the store we started from while the account is on another order. The
    // write refuses that either way, but refusing without routing leaves the
    // merchant on the provider step retrying a toast; the account step is
    // where the order they belong on is made current again. Nothing is created
    // for it — a recorded store is never a candidate for `bind` — so this is
    // not invariant (3)'s "never route an unknown into a billable order".
    return (
      started_for !== current_store.id ||
      Number(current_store.order_id) !== Number(useAuthStore().user?.current_order_id)
    );
  },
  // The order this installation already belongs to, if it has one: the order
  // `bind` named, or the one behind the store an in-app authorization was
  // started for. Having one means the way back is THAT order rather than
  // `bind` — which cannot recognise a store that does not carry a binding yet,
  // so for a reconnect it would create a second order or refuse outright. An
  // id we cannot resolve reads as undefined, which is unknown and not an
  // absence: the caller must not take it as licence to bind. FLOK-1716.
  installationOwnOrderId(): number | undefined {
    const installation = this.pending_tiendanube_installation;
    if (!installation) return undefined;
    if (installation.binding) return installation.binding.order_id;

    const started_for = installation.initiated_for_ecommerce_id;
    if (!started_for) return undefined;

    return useEcommerceStore().ecommerces.find(({ id }) => id === started_for)?.order_id;
  },
  // The OTHER question, and the one every site that WRITES must ask instead.
  // `installationNeedsOwnOrder` answers routing, where invariant (3) means an
  // unknown store must not send anyone off to create a billable order. Here it
  // means the opposite: a write may not proceed on a store we failed to read.
  // `getEcommerces` swallows its fetch error and `clear` leaves none, and
  // `completeTiendanubeInstallation` is resolved server-side from the
  // account's current order — so it needs no store of ours and would bind the
  // installation's token with nothing checked at all.
  //
  // It takes the id of the store whose values the caller is about to write —
  // a form seeded from a store is about THAT store, so its name, logo or URL
  // may not land on another one. `undefined` is a form seeded from no store:
  // it carries the merchant's own name and avatar, so the store provisioned
  // during the submit is still its target.
  installationMayWriteToCurrentStore(): (seeded_for_ecommerce_id?: number) => boolean {
    return (seeded_for_ecommerce_id) => {
      const installation = this.pending_tiendanube_installation;
      if (!installation) return true;

      // First, and for both branches: the store we would write to has to belong
      // to the order we have just read. The selected store is NOT self-
      // validating — `current_ecommerce_id` is re-pointed only for an order
      // whose summary loaded (see `ecommerce.store`), so after another tab
      // switches Flokee orders with a summary that failed, the list is fresh
      // while the selection still names the previous order's store. Its id can
      // still be the one this installation was started for, or the one its
      // binding names, while the server's current order — the order
      // `completeTiendanubeInstallation` resolves its target from — is another.
      // A selection we cannot tie to the current order is stale, which is
      // unknown, which refuses. Invariant (3).
      const current_store = useEcommerceStore().currentEcommerce;
      if (Number(current_store?.order_id) !== Number(useAuthStore().user?.current_order_id))
        return false;

      // The values being written are identity too, and a stale selection
      // leaks into them: a form seeded while the selection named the previous
      // order's store holds THAT store's name, logo or URL, and the forced
      // refresh then legitimately selects this installation's. Everything
      // above would approve it — the selected store is the right one — and
      // the write would copy one store's identity onto another. So the
      // question is not "is a valid store selected" but "is the selected
      // store the one these values were read for". Invariant (2), asked where
      // the write happens rather than at each form. FLOK-1717.
      if (seeded_for_ecommerce_id && seeded_for_ecommerce_id !== current_store?.id) return false;

      // Bound: the one store this installation may be written to is the one Flokee
      // named, compared by id. A store that would not load is not that store
      // either, so invariant (3) holds without a second check. FLOK-1716.
      if (installation.binding) {
        return current_store?.id === installation.binding.ecommerce_id;
      }

      return !this.installationNeedsOwnOrder;
    };
  },
} satisfies ThisType<InstallationHost>;

const installationActions = {
  async createTiendanubeInstallation(code: string, state?: string) {
    const { data: response } = await flokService.createTiendanubeInstallation(code, state);
    const installation = response?.data;

    if (!isTiendanubeInstallationResponse(installation)) {
      throw new Error('Flokee did not preserve the Tiendanube installation. Please try again.');
    }

    // Claim only the key this installation's own state names, and consume it.
    // An abandoned authorization therefore cannot hand its store to an
    // unrelated marketplace install arriving later in the same browser, and a
    // second authorization in another tab cannot take this one's.
    this.pending_tiendanube_installation = {
      ...installation,
      initiated_for_ecommerce_id: state ? readTiendanubeAuthorization(state) : undefined,
    };

    if (state) localStorage.removeItem(tiendanubeAuthorizationKey(state));

    return installation;
  },
  // Called where an in-app authorization starts, which is the only moment the
  // store it is for is known: it round-trips through Tiendanube, and what
  // comes back names a Tiendanube store, not one of ours. Paired with the
  // state so only the authorization that minted it can claim this store.
  // Invariant (2): the caller passes the store it captured before minting the
  // state, because reading the current one here would record whatever another
  // tab selected while that request was in flight.
  recordTiendanubeAuthorization(state: string, ecommerce_id?: number, expires_at?: string) {
    if (!ecommerce_id) return;

    // Swept on write rather than on a timer: an authorization abandoned
    // before its callback leaves its key behind, and nothing else prunes it.
    // What says it is dead is the expiry Flokee issued WITH THE STATE, not a
    // clock of ours: once that has passed Tiendanube cannot return with that
    // state, so the association cannot be claimed. It used to assume the
    // installation ticket's hour, which Flokee only issues at the callback — so a
    // state valid for longer than an hour had its store swept by the next
    // authorization, and the first one came back looking like a marketplace
    // install. An entry whose expiry we cannot read is left alone: only the
    // state it is named after can ever claim it, so it costs a few bytes,
    // and removing it is the mistake this replaces.
    Object.keys(localStorage)
      .filter((key) => key.startsWith(TIENDANUBE_AUTHORIZATION_KEY_PREFIX))
      .forEach((key) => {
        try {
          const entry = JSON.parse(localStorage.getItem(key) ?? 'null') ?? {};
          if (Date.parse(entry.expires_at) <= Date.now()) localStorage.removeItem(key);
        } catch {
          localStorage.removeItem(key);
        }
      });

    localStorage.setItem(
      tiendanubeAuthorizationKey(state),
      JSON.stringify({ ecommerce_id, expires_at }),
    );
  },
  // Invariant (1): the reads both identity getters must sit on when they are
  // asked immediately before a write, rather than at route entry. The store
  // list goes with the user because it is selected by the account's current
  // order and `getEcommerces` reuses the cached one for an unchanged order id
  // — user first, then stores, then decide. Nothing pending means nothing to
  // check, and then it reads nothing.
  async refreshInstallationStoreState(): Promise<void> {
    if (!this.pending_tiendanube_installation) return;

    // Evict first, read second: `getCurrentUser` ends in `init()`, which loads
    // the store list itself for a Flokee order, so clearing afterwards threw
    // that away — along with the configuration tasks and plan approvals it had
    // just fetched — and then loaded the list a second time. This way the
    // user's own reload IS the fresh read, and there is one of each.
    //
    // The list is emptied and the QUERY evicted, but `clear()` is not used:
    // it writes `current_ecommerce_id = 0`, and that id lives in
    // `localStorage`, so it is shared live with every other tab — a tab
    // sitting on the provider step would watch its store disappear and
    // redirect itself off a form in progress. The list itself is per-tab, and
    // emptying it is what makes a failed reload refuse: `getEcommerces`
    // swallows its error, so an untouched list would leave the previous store
    // in place for `installationMayWriteToCurrentStore` to approve while the
    // server had already moved on. Invariant (3).
    useEcommerceStore().ecommerces = [];
    await queryClient.removeQueries({ queryKey: ['ecommerces'] });
    await useAuthStore().refreshCurrentUser();

    // `init()` loads the store list only when the account's current order is
    // a Flokee one, and an installation can be sitting on a Cleepo order —
    // FLOK-1714's own shape, and what another tab does during OAuth. The
    // stores come back by ACCOUNT, so asking for the list directly is what
    // finds the store this installation was started for; without it the list
    // stays empty, the store's order reads as unknown, and the merchant is
    // told their own store does not exist on every retry. A list that is
    // already loaded was loaded by `init()`: asking again costs a read for
    // nothing. It cannot disturb another tab either — `getEcommerces` writes
    // the shared `current_ecommerce_id` only for an order its summary names as
    // Flokee's, which is never the order this fallback exists for.
    if (!useEcommerceStore().ecommerces.length) await useEcommerceStore().getEcommerces();
  },
  getPendingTiendanubeInstallation() {
    return this.pending_tiendanube_installation;
  },
  clearPendingTiendanubeInstallation() {
    this.pending_tiendanube_installation = undefined;
  },
  getPostAuthenticationRouteName(defaultRouteName: string): string {
    if (!this.getPendingTiendanubeInstallation()) {
      return defaultRouteName;
    }

    return useAuthStore().user?.current_account_id &&
      useAuthStore().user?.current_order_id &&
      !this.installationNeedsOwnOrder
      ? 'WizardSelectEcommerceProviderView'
      : 'WizardCreateAccountView';
  },
  async bindTiendanubeInstallation(plan_id: number, offer_id?: number) {
    const installation = this.getPendingTiendanubeInstallation();
    if (!installation) {
      throw new Error('There is no pending Tiendanube installation to bind.');
    }

    const initiatedByUserId = useAuthStore().user?.id;
    const account_id = useAuthStore().user?.current_account_id;
    if (!account_id) {
      throw new Error('Select an account before connecting Tiendanube.');
    }

    const { data: response } = await flokService.bindTiendanubeInstallation(
      installation.installation_token,
      account_id,
      plan_id,
      offer_id,
    );
    const binding = response?.data;

    if (!isTiendanubeBindResponse(binding)) {
      throw new Error('Flokee did not bind the Tiendanube installation. Please try again.');
    }

    if (
      useAuthStore().user?.id !== initiatedByUserId ||
      this.pending_tiendanube_installation?.installation_token !== installation.installation_token
    ) {
      throw new Error('The installation session changed. Resume the original installation.');
    }
    this.pending_tiendanube_installation = { ...installation, binding };

    // The returned order is what every later step must act on, and this is
    // what carries it: the ecommerce list is selected by the account's current
    // order, so making the bound order current is how the merchant and the
    // whole app end up inside the order they just installed. The identity
    // getters above still compare against the binding itself rather than
    // against whatever is current, which is what makes a tab that moves it
    // afterwards visible instead of silently followed.
    if (useAuthStore().user?.current_account_id !== account_id) {
      throw new Error(
        'The selected account changed. Return to the installation account to continue.',
      );
    }
    await useAuthStore().setCurrentOrder(binding.order_id);

    return binding;
  },
  async completeTiendanubeInstallation(ecommerce_id: number) {
    const installation = this.getPendingTiendanubeInstallation();
    if (!installation) {
      throw new Error('There is no pending Tiendanube installation to complete.');
    }

    try {
      const { data: response } = await flokService.completeTiendanubeInstallation(
        installation.installation_token,
        ecommerce_id,
      );
      const connection = response?.data;

      if (!isTiendanubeConnectResponse(connection)) {
        throw new Error('Flokee did not confirm the Tiendanube connection. Please try again.');
      }

      if (
        this.pending_tiendanube_installation?.installation_token === installation.installation_token
      ) {
        this.clearPendingTiendanubeInstallation();
      }

      return connection;
    } catch (error) {
      const { status } = handleAxiosError(error);
      if (
        status === '410' &&
        this.pending_tiendanube_installation?.installation_token === installation.installation_token
      ) {
        this.clearPendingTiendanubeInstallation();
      }

      throw error;
    }
  },
} satisfies ThisType<InstallationHost>;

export const useTiendanubeInstallationStore = defineStore('tiendanubeInstallation', {
  state: installationState,
  getters: installationGetters,
  actions: installationActions,
});
